import { PopupMakerElementDataType } from '@/components/sections/builder/elements/body/popup-maker/type';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { ElementType } from '@/stores/builder';
import React from 'react';

type PopupBannerProps = {
  typeElement?: string;
  isOpen: boolean;
  content: PopupMakerElementDataType;
  preview?: boolean;
  themeId?: string;
  childs?: ElementType[];
  elements?: ElementType[];
  setIsOpen: (isOpen: boolean) => void;
};

export default function PopupBanner({
  typeElement,
  isOpen,
  content,
  setIsOpen,
  preview,
  childs,
  themeId,
  elements,
}: PopupBannerProps) {
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const isMobile = useMediaQuery(960);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, []);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-[99999] flex h-full w-full items-center justify-center overflow-y-auto p-20 transition-opacity duration-300',
          isOpen ? 'visible opacity-100' : 'invisible opacity-0'
        )}
        style={{ backgroundColor: `rgba(0, 0, 0, ${content.overlay ?? 0})` }}
        onClick={() => setIsOpen(false)}
        aria-hidden='true'
        role='presentation'
      >
        <div
          className={cn(
            'bg-color-scheme fixed left-[50%] top-[50%] h-full w-full max-w-[90vw] translate-x-[-50%] translate-y-[-50%] transform border-transparent shadow-lg outline-none ring-0 transition-all duration-300 sm:max-w-xl',
            isOpen
              ? 'animate-in fade-in-0 zoom-in-95 slide-in-from-left-1/2 slide-in-from-top-[48%]'
              : 'animate-out fade-out-0 zoom-out-95 slide-out-to-left-1/2 slide-out-to-top-[48%]',
            content.size !== 'custom' && content.size === 'small'
              ? 'md:max-h-[24rem] md:max-w-sm'
              : content.size === 'medium'
              ? 'md:max-h-[28rem] md:max-w-md'
              : content.size === 'xlarge'
              ? 'md:max-h-[36rem] md:max-w-xl'
              : 'md:max-h-[32rem] md:max-w-lg',
            content.size === 'custom' && !isMobile && 'box-size-custom',
            isMobile ? 'max-h-[90vh]' : 'max-h-[28rem]'
          )}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          tabIndex={isOpen ? 0 : -1}
          role='dialog'
          aria-modal='true'
          aria-labelledby='popup-title'
          style={{
            padding: `${content.padding}px`,
            borderRadius: `${content.rounded}px`,
          }}
        >
          <div
            ref={contentRef}
            className={cn(
              'relative h-full w-full overflow-y-auto',
              content.layout === 'vertical' && (childs?.length || 0) === 1
                ? 'grid grid-cols-1'
                : content.layout === 'vertical' && (childs?.length || 0) > 1
                ? 'flex flex-col'
                : content.layout === 'horizontal' && (childs?.length || 0) > 1
                ? 'flex flex-col md:flex-row'
                : 'grid-cols-1',
              isMobile && isOverflowing && 'justify-start'
            )}
            style={{
              borderRadius: 'inherit',
            }}
          >
            {childs?.map((child) => (
              <WrapperChild
                className='relative h-full w-full'
                key={child.id}
                child={{
                  ...child,
                  parentData: content,
                  themeId: themeId,
                  elements: elements,
                }}
                parentType={typeElement as string}
                preview={preview}
              />
            ))}
          </div>
          <button
            className='absolute flex items-center justify-center px-2 py-1 text-xl outline-none ring-0 transition-colors duration-200 focus:outline-none focus:ring-0 active:outline-none active:ring-0'
            style={{
              top: 2,
              right: 2,
              backgroundColor: 'transparent',
              color: content.colorSchemeSection?.btnSolidBackgroud || 'black',
            }}
            onClick={() => setIsOpen(false)}
            aria-label='Close popup'
            title='Close'
          >
            ✕
          </button>
        </div>
      </div>

      <style jsx>
        {`
          .box-size-custom {
            max-width: ${content.customWidth}px;
            max-height: ${content.customHeight}px;
          }
          .bg-color-scheme {
            background-color: ${content.colorSchemeSection && content.colorSchemeSection.background
              ? content.colorSchemeSection.background
              : 'white'};
            color: ${content.colorSchemeSection && content.colorSchemeSection.text
              ? content.colorSchemeSection.text
              : 'black'};
          }
        `}
      </style>
    </>
  );
}
